import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auth, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { getClub, updateClub, updateClubTeams, getTeamSchedules } from '../utils/clubs';
import { getUserProfile } from '../utils/database';
import { AVAILABLE_AGE_GROUPS, generateTeamId } from '../utils/ageGroups';
import TeamScheduleEditor from '../components/TeamScheduleEditor';
import ProfilePictureCropper from '../components/ProfilePictureCropper';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import '../styles/Dashboard.css';
import '../styles/ClubSignup.css';

function ClubManage() {
  const { userId, clubId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [club, setClub] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markupPerMonth, setMarkupPerMonth] = useState(0);

  const [teamInputs, setTeamInputs] = useState({});

  // Upload states
  const [logoFile, setLogoFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [w9File, setW9File] = useState(null);
  const [uploading, setUploading] = useState(null); // 'logo' | 'banner' | 'w9' | null

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || currentUser.uid !== userId) {
        navigate('/signin', { replace: true });
        return;
      }
      const userData = await getUserProfile(userId);
      setUser(userData);
    });
    return () => unsubscribe();
  }, [userId, navigate]);

  useEffect(() => {
    if (!clubId || !user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const clubData = await getClub(clubId);
        if (cancelled) return;
        const isOwner = clubData && (clubData.ownerId === userId || user.ownedClubIds?.includes(clubId));
        if (!clubData || !isOwner) {
          navigate(`/${userId}/dashboard`, { replace: true });
          return;
        }
        setClub(clubData);
        setMarkupPerMonth(clubData.markupPerMonth ?? 0);
      } catch (err) {
        console.error('[ClubManage] Club load error:', err);
        if (!cancelled) navigate(`/${userId}/dashboard`, { replace: true });
        return;
      } finally {
        setLoading(false);
      }

      // Non-critical data — don't redirect on failure
      try {
        const teamScheds = await getTeamSchedules(clubId);
        if (!cancelled) setSchedules(teamScheds);
      } catch (err) {
        console.error('[ClubManage] Schedules load error (non-critical):', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clubId, userId, user, navigate]);

  const handleClubUpdate = async (updates) => {
    try {
      const updated = await updateClub(clubId, updates);
      setClub(updated);
    } catch (err) {
      console.error('[ClubManage] Update error:', err);
    }
  };

  const teams = club?.teams || [];
  const selectedAgeGroups = [...new Set(teams.map((t) => t.ageGroup))];

  const toggleAgeGroup = async (ag) => {
    let nextTeams;
    if (selectedAgeGroups.includes(ag)) {
      nextTeams = teams.filter((t) => t.ageGroup !== ag);
    } else {
      nextTeams = [...teams, { id: generateTeamId(ag, 'main'), ageGroup: ag, label: 'Main' }];
    }
    await updateClubTeams(clubId, nextTeams);
    setClub((prev) => ({ ...prev, teams: nextTeams }));
  };

  const handleAddTeam = async (ag) => {
    const label = (teamInputs[ag] || '').trim();
    if (!label) return;
    const id = generateTeamId(ag, label);
    if (teams.some((t) => t.id === id)) return;
    const nextTeams = [...teams, { id, ageGroup: ag, label }];
    await updateClubTeams(clubId, nextTeams);
    setClub((prev) => ({ ...prev, teams: nextTeams }));
    setTeamInputs((prev) => ({ ...prev, [ag]: '' }));
  };

  const handleRemoveTeam = async (ag, teamId) => {
    const nextTeams = teams.filter((t) => t.id !== teamId);
    const agTeamsLeft = nextTeams.filter((t) => t.ageGroup === ag);
    const finalTeams = agTeamsLeft.length === 0
      ? nextTeams.filter((t) => t.ageGroup !== ag)
      : nextTeams;
    await updateClubTeams(clubId, finalTeams);
    setClub((prev) => ({ ...prev, teams: finalTeams }));
  };

  // Upload handlers
  const handleLogoChange = (file) => {
    setLogoFile(file);
  };

  const handleLogoSave = async () => {
    if (!logoFile) return;
    setUploading('logo');
    try {
      const ext = logoFile.name.split('.').pop();
      const storageRef = ref(storage, `clubs/${clubId}/logo.${ext}`);
      await uploadBytes(storageRef, logoFile);
      const url = await getDownloadURL(storageRef);
      await handleClubUpdate({ logoUrl: url });
      setLogoFile(null);
    } catch (err) {
      console.error('[ClubManage] Logo upload error:', err);
    } finally {
      setUploading(null);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setBannerPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleBannerSave = async () => {
    if (!bannerFile) return;
    setUploading('banner');
    try {
      const ext = bannerFile.name.split('.').pop();
      const storageRef = ref(storage, `clubs/${clubId}/banner.${ext}`);
      await uploadBytes(storageRef, bannerFile);
      const url = await getDownloadURL(storageRef);
      await handleClubUpdate({ bannerUrl: url });
      setBannerFile(null);
      setBannerPreview(null);
    } catch (err) {
      console.error('[ClubManage] Banner upload error:', err);
    } finally {
      setUploading(null);
    }
  };

  const handleW9Change = (e) => {
    const file = e.target.files[0];
    if (file) setW9File(file);
  };

  const handleW9Upload = async () => {
    if (!w9File) return;
    setUploading('w9');
    try {
      const ext = w9File.name.split('.').pop();
      const storagePath = `club-private/${clubId}/w9.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, w9File);
      await handleClubUpdate({ w9StoragePath: storagePath, w9Status: 'pending' });
      setW9File(null);
    } catch (err) {
      console.error('[ClubManage] W-9 upload error:', err);
    } finally {
      setUploading(null);
    }
  };

  const landingUrl = club ? `${window.location.origin}/clubs/${club.slug}` : '';
  const markupBlocked = markupPerMonth > 0 && (!club?.stripeAccountId || club?.w9Status !== 'approved');

  const scheduleMap = {};
  schedules.forEach((s) => { scheduleMap[s.teamId] = s; });

  const clubName = club?.name || '';
  const dataLoaded = !loading && club;

  return (
    <div className="dashboard-page has-bottom-nav">
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />
      <DashboardSidebar userId={userId} user={user} />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            {clubName ? (
              <h1 className="dashboard-content-loaded">{clubName}</h1>
            ) : (
              <div className="dashboard-skeleton-title" />
            )}
          </div>
        </div>

        <div className="club-dashboard-nav">
          <Link to={`/${userId}/club/${clubId}`} className="club-dashboard-nav-btn">
            Teams
          </Link>
          <Link to={`/${userId}/club/${clubId}/reports`} className="club-dashboard-nav-btn">
            Reports
          </Link>
          <Link to={`/${userId}/club/${clubId}/manage`} className="club-dashboard-nav-btn club-dashboard-nav-right active">
            Club Settings
          </Link>
        </div>

        <div className="settings-content">
          {!dataLoaded ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="dashboard-card club-skeleton-card">
                  <div className="club-skeleton-stat" />
                  <div className="club-skeleton-row" />
                  <div className="club-skeleton-row" />
                </div>
              ))}
            </>
          ) : (
            <>

          {/* Landing Page */}
          <div className="dashboard-card">
            <div className="settings-form-item">
              <label className="settings-form-label">Landing Page</label>
              <div className="settings-form-control">
                <input
                  type="text"
                  readOnly
                  value={landingUrl}
                  className="settings-input"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="c-ripple club-copy-btn"
                  onClick={() => navigator.clipboard.writeText(landingUrl)}
                >
                  Copy
                </button>
              </div>
              <p className="settings-description">Share this link with your athletes to sign up through your club.</p>
            </div>
          </div>

          {/* Club Logo & Banner */}
          <div className="dashboard-card">
            <div className="settings-form-item">
              <label className="settings-form-label">Club Logo</label>
              <p className="settings-description">Displayed on your landing page. Use a square image for best results.</p>
              <ProfilePictureCropper
                currentImageUrl={club.logoUrl || null}
                onImageChange={(file) => handleLogoChange(file)}
                shape="circle"
              />
              {logoFile && (
                <button
                  type="button"
                  className="c-ripple club-copy-btn"
                  style={{ marginTop: '12px' }}
                  onClick={handleLogoSave}
                  disabled={uploading === 'logo'}
                >
                  {uploading === 'logo' ? 'Uploading...' : 'Save Logo'}
                </button>
              )}
            </div>

            <div className="settings-form-item" style={{ marginTop: '24px' }}>
              <label className="settings-form-label">Banner Photo</label>
              <p className="settings-description">Shown as the hero background on your landing page. Recommended: 1600×600.</p>
              {(bannerPreview || club.bannerUrl) && (
                <div className="club-banner-preview">
                  <img src={bannerPreview || club.bannerUrl} alt="Banner" />
                </div>
              )}
              <div className="settings-form-control" style={{ marginTop: '8px' }}>
                <label className="c-ripple club-copy-btn" style={{ cursor: 'pointer' }}>
                  {club.bannerUrl ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerChange}
                    style={{ display: 'none' }}
                  />
                </label>
                {bannerFile && (
                  <button
                    type="button"
                    className="c-ripple club-copy-btn"
                    onClick={handleBannerSave}
                    disabled={uploading === 'banner'}
                  >
                    {uploading === 'banner' ? 'Uploading...' : 'Save Banner'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="dashboard-card">
            <div className="settings-form-item">
              <label className="settings-form-label">Pricing</label>
              <p className="settings-description">
                Base rate is $4/mo per athlete. Add up to $4 on top — your club keeps the difference.
              </p>
              <div className="club-pricing-equation">
                <span className="club-pricing-base">$4</span>
                <span className="club-pricing-operator">+</span>
                <select
                  value={markupPerMonth}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (val > 0 && markupBlocked) return;
                    setMarkupPerMonth(val);
                    handleClubUpdate({ markupPerMonth: val });
                  }}
                  className="club-pricing-select"
                  disabled={markupBlocked}
                >
                  {[0, 1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>${n}</option>
                  ))}
                </select>
                <span className="club-pricing-operator">=</span>
                <span className="club-pricing-total">${4 + markupPerMonth}/mo</span>
                <span className="club-pricing-label">per athlete</span>
              </div>
              {markupBlocked && (
                <p className="club-warning-text">
                  Connect Stripe and upload W-9 to receive markup payouts.
                </p>
              )}
            </div>
          </div>

          {/* Payments */}
          <div className="dashboard-card">
            <div className="settings-form-item">
              <label className="settings-form-label">Stripe Connect</label>
              <div className="settings-form-control">
                <span className={`club-status-badge ${club.stripeAccountId ? 'connected' : ''}`}>
                  {club.stripeAccountId ? 'Connected' : 'Not connected'}
                </span>
              </div>
              <p className="settings-description">Required to receive markup payouts. (Coming soon)</p>
            </div>

            <div className="settings-form-item">
              <label className="settings-form-label">W-9</label>
              <div className="settings-form-control">
                <span className={`club-status-badge ${club.w9Status === 'approved' ? 'connected' : club.w9Status === 'pending' ? '' : ''}`}>
                  {club.w9Status === 'approved' ? 'Approved' : club.w9Status === 'pending' ? 'Pending Review' : 'Not uploaded'}
                </span>
              </div>
              {!club.w9Status || club.w9Status === 'rejected' ? (
                <div className="settings-form-control" style={{ marginTop: '8px' }}>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleW9Change}
                    style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}
                  />
                  {w9File && (
                    <button
                      type="button"
                      className="c-ripple club-copy-btn"
                      onClick={handleW9Upload}
                      disabled={uploading === 'w9'}
                    >
                      {uploading === 'w9' ? 'Uploading...' : 'Upload W-9'}
                    </button>
                  )}
                </div>
              ) : null}
              <p className="settings-description">Required to receive markup payouts.</p>
            </div>
          </div>

          {/* Teams & Age Groups */}
          <div className="dashboard-card">
            <div className="settings-form-item">
              <label className="settings-form-label">Age Groups</label>
              <p className="settings-description">Select which age groups your club offers.</p>
              <div className="club-age-group-grid">
                {AVAILABLE_AGE_GROUPS.map((ag) => (
                  <button
                    key={ag}
                    type="button"
                    className={`club-age-group-chip ${selectedAgeGroups.includes(ag) ? 'selected' : ''}`}
                    onClick={() => toggleAgeGroup(ag)}
                  >
                    {ag}
                  </button>
                ))}
              </div>
            </div>

            {selectedAgeGroups.length > 0 && (
              <div className="settings-form-item">
                <label className="settings-form-label">Teams</label>
                <div className="club-teams-sections">
                  {AVAILABLE_AGE_GROUPS.filter((ag) => selectedAgeGroups.includes(ag)).map((ag) => {
                    const agTeams = teams.filter((t) => t.ageGroup === ag);
                    return (
                      <div key={ag} className="club-team-section">
                        <h4 className="club-team-section-title">{ag}</h4>
                        <div className="club-team-tags">
                          {agTeams.map((t) => (
                            <span key={t.id} className="club-team-tag">
                              {t.label}
                              <button type="button" className="club-team-tag-remove" onClick={() => handleRemoveTeam(ag, t.id)}>&times;</button>
                            </span>
                          ))}
                        </div>
                        <div className="club-team-add-row">
                          <input
                            type="text"
                            className="club-team-input"
                            placeholder="e.g. 1's, Gold, National"
                            value={teamInputs[ag] || ''}
                            onChange={(e) => setTeamInputs((prev) => ({ ...prev, [ag]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTeam(ag);
                              }
                            }}
                          />
                          <button type="button" className="club-team-add-btn" onClick={() => handleAddTeam(ag)}>Add</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Team Schedules */}
          {teams.length > 0 && (
            <div className="dashboard-card">
              <div className="settings-form-item">
                <label className="settings-form-label">Team Schedules <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400, fontSize: '14px' }}>(optional)</span></label>
                <p className="settings-description">Set practice and game days for each team. This helps the program schedule around your season.</p>
              </div>
              <div className="club-schedule-grid">
                {[...teams].sort((a, b) => {
                  const ai = AVAILABLE_AGE_GROUPS.indexOf(a.ageGroup);
                  const bi = AVAILABLE_AGE_GROUPS.indexOf(b.ageGroup);
                  if (ai !== bi) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                  return (a.label || '').localeCompare(b.label || '');
                }).map((team) => (
                  <TeamScheduleEditor
                    key={team.id}
                    clubId={clubId}
                    team={team}
                    existingSchedule={scheduleMap[team.id] || null}
                    onSaved={async () => {
                      const fresh = await getTeamSchedules(clubId);
                      setSchedules(fresh);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          </>
          )}
        </div>
      </div>
      <BottomNav userId={userId} user={user} />
    </div>
  );
}

export default ClubManage;
